import { useParams } from "react-router-dom";
import FormType from "enums/FormType";
import { useEffect, useState } from "react";
import FormEditorContainer from "features/qualityForm/formEditor/FormEditorContainer";

import {
    loadForm,
    loadFormNames,
    loadAgreementRate,
    selectLoadError,
} from "features/qualityForm/qualityFormSlice";
import {
    getFavoriteTemplates,
    getTemplates,
} from "features/templates/templateSlice";
import { fetchTopics } from "features/topics/topicsSlice";
import { copyFormSliceActions } from "features/qualityForm/formEditor/copyFormSlice";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectFeatureToggle } from "features/featureToggles/featureToggleSlice";
import FeatureToggle from "enums/FeatureToggle";
import { NotFound } from "./NotFound";
import { EvaluationFormEvaluationFormTypeEnum } from "@genesys/cloud-frontend/models";
import { selectHasMultipleSelectQuestions } from "features/qualityForm/qualityFormValidations";
import { NotAuthorized } from "./NotAuthorized";
import embeddingService from "services/embeddingService";
import toastService from "services/toastService";
import {
    getFormsListRoutePath,
    getOldFormsListRoutePath,
} from "util/routeUtils";
import {
    selectAutomatedEvaluationFeatureEnabled,
    selectForm,
    selectIsAutomatedEvaluationForm,
} from "features/qualityForm/qualityFormSlices";
import { selectShouldLoadAgreementRate } from "features/qualityForm/qualityFormAgreementRateSelectors";
import { loadMaxAiScoringQuestionsOuterLimit } from "features/qualityForm/outerLimitSlice";

export function EvaluationForm({
    evaluationFormType,
}: {
    evaluationFormType?: EvaluationFormEvaluationFormTypeEnum;
}) {
    const { evaluationFormId } = useParams();
    const [hasShownToast, setHasShownToast] = useState(false);

    const isAutomatedEvaluationCompletionFeatureEnabled = useAppSelector(
        selectAutomatedEvaluationFeatureEnabled
    );
    const isMultipleSelectFTEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.MULTIPLE_SELECT)
    );

    const isAutomatedEvaluationForm = useAppSelector(
        selectIsAutomatedEvaluationForm
    );

    const shouldLoadAgreementRate = useAppSelector(
        selectShouldLoadAgreementRate
    );

    const loadError = useAppSelector(selectLoadError);
    const isAdminUIDecompEnabled = useAppSelector((state) =>
        selectFeatureToggle(state, FeatureToggle.ADMIN_UI_DECOMPOSITION)
    );

    const isAiScoringQuestionOuterLimitEnabled = useAppSelector((state) =>
        selectFeatureToggle(
            state,
            FeatureToggle.MAX_AI_SCORING_QUESTION_OUTER_LIMIT
        )
    );

    const dispatch = useAppDispatch();

    useEffect(() => {
        if (isAiScoringQuestionOuterLimitEnabled) {
            dispatch(loadMaxAiScoringQuestionsOuterLimit());
        }
    }, [dispatch, isAiScoringQuestionOuterLimitEnabled]);

    useEffect(() => {
        if (loadError?.formType) {
            const path = isAdminUIDecompEnabled
                ? getFormsListRoutePath(loadError.formType)
                : getOldFormsListRoutePath(loadError.formType);
            embeddingService.requestNavigation(path);
        }
    }, [loadError, isAdminUIDecompEnabled]);
    useEffect(() => {
        if (evaluationFormId) {
            // For new forms, set evaluationFormType based on path
            dispatch(
                loadForm({
                    formType: FormType.EVALUATION,
                    formId: evaluationFormId,
                    ...(evaluationFormId === "new"
                        ? { evaluationFormType }
                        : {}),
                })
            );
            dispatch(getTemplates({ isAutomatedEvaluationForm }));
            dispatch(getFavoriteTemplates());
            dispatch(fetchTopics());
            dispatch(loadFormNames(FormType.EVALUATION));
            dispatch(copyFormSliceActions.initializeCopyingViewMode());
        }
    }, [
        dispatch,
        evaluationFormId,
        isAutomatedEvaluationCompletionFeatureEnabled,
        evaluationFormType,
        isAutomatedEvaluationForm,
    ]);

    const isFormLoaded = useAppSelector(
        (state) => selectForm(state) !== undefined
    );

    useEffect(() => {
        if (isFormLoaded && shouldLoadAgreementRate && evaluationFormId) {
            dispatch(loadAgreementRate({ currentFormId: evaluationFormId }));
        }
    }, [isFormLoaded, shouldLoadAgreementRate, evaluationFormId, dispatch]);

    const hasMultipleSelectQuestion = useAppSelector(
        selectHasMultipleSelectQuestions
    );

    const cannotOpenEvaluationFormWithMSQ =
        !isMultipleSelectFTEnabled && hasMultipleSelectQuestion;

    useEffect(() => {
        if (cannotOpenEvaluationFormWithMSQ && !hasShownToast) {
            toastService.notifyMinorAlert(
                "composer.evaluations.evaluation.failToOpenForm.title",
                {
                    bodyMessageKey:
                        "composer.evaluations.evaluation.failToOpenForm.message",
                }
            );
            setHasShownToast(true);
        }
    }, [cannotOpenEvaluationFormWithMSQ, hasShownToast]);

    // If the feature is disabled AND we do have an evaluationFormType,
    // show 404; otherwise, show the form.
    if (
        !isAutomatedEvaluationCompletionFeatureEnabled &&
        evaluationFormType != null
    ) {
        return <NotFound />;
    }

    // check if the form has multiple select questions?
    if (cannotOpenEvaluationFormWithMSQ) {
        return <NotAuthorized />;
    }

    // If there's a load error, don't render anything (redirect will happen from Redux)
    if (loadError) {
        return null;
    }

    return <FormEditorContainer formType={FormType.EVALUATION} />;
}
